import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { FilterSpecification } from 'maplibre-gl';
import { filter, startWith } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { managementDataRegexp, managementOrganizationRegexp } from '@connect-services/client/guards/can-navigate.guard';
import { PermissionAliasEnum, Project } from '@connect-services/client/sdk/api';
import { BroadcastChannelService } from '@connect-services/client/services/broadcast-channel.service';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { EditModeLeaveService } from '@connect-services/client/services/edit-mode-leave.service';
import { LocationService } from '@connect-services/client/services/location.service';
import { SelectionService } from '@connect-services/client/services/map/selection/selection.service';
import { MapClickIndicatorService } from '@connect-services/client/services/map-click-indicator.service';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { MapFilterService } from '@connect-services/client/services/map-filter.service';
import { MapHighlightEntityService } from '@connect-services/client/services/map-highlight-entity.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { ProjectService } from '@connect-services/client/services/project.service';
import { ShapingProjectService } from '@connect-services/client/services/project/shaping/shaping-project.service';
import { CreateProjectShapingStateService } from '@connect-services/client/services/project/shaping/shaping-state/create-project-shaping-state.service';
import { SelectSynopticService } from '@connect-services/client/services/synoptic/select-synoptic.service';
import { TableEditModeService } from '@connect-services/client/services/table-edit-mode.service';
import { HasSomePermissionsDirective } from '@connect-services/src/app/directives/has-some-permissions.directive';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [HasSomePermissionsDirective, RessourcesModule, SelectComponent],
    selector: 'app-project-select',
    styleUrl: './project-select.component.scss',
    templateUrl: './project-select.component.html',
})
export class ProjectSelectComponent {
    public defaultProjectColor: string = 'blue';
    public readonly projectOptions = computed<SelectOption<Project>[]>(() => {
        return this.projectService.projects().map((project: Project) => ({
            label: project.name,
            trailingIcon: ['fas', 'circle-small'],
            value: project,
        }));
    });
    public readonly show = signal<boolean>(true);

    protected readonly PermissionAliasEnum = PermissionAliasEnum;
    protected readonly projectService = inject(ProjectService);
    protected readonly searchValue = signal<string>('');
    protected readonly selectedProject = computed<Project | undefined>(() => {
        const projectId = this.projectBloc.value()?.id;
        const projects = this.projectService.projects();

        const currentProject = projects.find((project: Project) => project.id === projectId);

        return currentProject ?? projects[0];
    });
    protected readonly translocoService = inject(TranslocoService);

    private readonly broadcastChannelService = inject(BroadcastChannelService);
    private readonly createFieldInterventionService = inject(CreateFieldInterventionService);
    private readonly editModeLeaveService = inject(EditModeLeaveService);
    private readonly locationService = inject(LocationService);
    private readonly mapClickIndicatorService = inject(MapClickIndicatorService);
    private readonly mapEditModeService = inject(MapEditModeService);
    private readonly mapFilterService = inject(MapFilterService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly router = inject(Router);
    private readonly selectionService = inject(SelectionService);
    private readonly selectSynopticService = inject(SelectSynopticService);
    private readonly shapingProjectService = inject(ShapingProjectService);
    private readonly tableEditModeService = inject(TableEditModeService);

    public constructor() {
        this.router.events
            .pipe(
                startWith(null),
                filter((event: Event | null) => event === null || event instanceof NavigationEnd),
            )
            .subscribe(() => {
                this.show.set(!this.isManagementUrl());
            });

        effect(async () => {
            const query = (this.searchValue() ?? '').toString().trim().toLowerCase();
            this.projectService.projects.set(await this.projectService.getProjects(query));
        });
    }

    public async changeProject(projectId: string): Promise<void> {
        this.mapClickIndicatorService.removeIndicatorSource();
        this.mapFilterService.initialFilterByLayer = new Map<string, FilterSpecification | undefined>();
        await this.mapSourceService.removeConnieSource();
        await this.selectionService.clearAllSelections(true);

        this.selectSynopticService.resetAll();
        this.createFieldInterventionService.resetData();
        await this.locationService.changeProjectRoute(projectId);
        this.broadcastChannelService.closeAllExternalWindows();

        this.reloadCopilotWithConfig();
    }

    public handleCreateProject(): void {
        this.shapingProjectService.openShapingProjectModal(CreateProjectShapingStateService);
    }

    public async handleProjectSelection(project: Project): Promise<void> {
        if (!project || project.id === this.projectBloc.value()?.id) {
            return;
        }

        const leaveEditModeResult = await this.editModeLeaveService.requestLeaveEditMode();
        if (!leaveEditModeResult.canLeave) {
            return;
        }

        await this.mapEditModeService.forceExitEditModeUi();
        this.tableEditModeService.forceExitEditModeUi();

        MapSourceService.isMapOrProjectFirstLoading.set(true);
        MapHighlightEntityService.isProjectChanged.set(true);
        await this.changeProject(project.id);
    }

    public async onOptionSelected(values: Project[]): Promise<void> {
        const project = values[0];
        if (!project) {
            return;
        }

        this.resetSearch();

        if (project.id === 'create_project') {
            this.handleCreateProject();

            return;
        }

        await this.handleProjectSelection(project);
    }

    public resetSearch(): void {
        this.searchValue.set('');
    }

    private isManagementUrl(): boolean {
        return managementDataRegexp.test(this.router.url) || managementOrganizationRegexp.test(this.router.url);
    }

    private reloadCopilotWithConfig(): void {
        let tries = 0;
        const maxTries = 40;
        const tryRestore = (): void => {
            tries++;
            try {
                if (typeof window.clearChainlitCopilotThreadId !== 'function') {
                    return;
                }

                window.clearChainlitCopilotThreadId();
            } catch {
                /* empty */
            }
            if (tries < maxTries) {
                requestAnimationFrame(tryRestore);
            }
        };
        requestAnimationFrame(tryRestore);
    }
}
